import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'

interface HomeCircleAnimatProps {
	data?: any
}

export const fields: string[] = []

export default function HomeCircleAnimat({ data }: HomeCircleAnimatProps) {
	const [progress, setProgress] = useState(0)
	const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down')
	const [lastScrollY, setLastScrollY] = useState(0)
	const sectionRef = useRef<HTMLElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	
	useEffect(() => {
		const section = sectionRef.current
		const container = containerRef.current
		if (!section || !container) return

		const calculateProgress = () => {
			const rect = container.getBoundingClientRect()
			const windowHeight = window.innerHeight
			const totalDistance = windowHeight * 2 // Общая дистанция для анимации

			// Начинаем анимацию когда верх секции достигает верха экрана
			let progress = 0
			if (rect.top <= 0) {
				progress = Math.abs(rect.top) / totalDistance
			}

			// Ограничиваем прогресс от 0 до 1
			progress = Math.max(0, Math.min(1, progress))
			setProgress(progress)
		}

		const handleScroll = () => {
			const currentScrollY = window.scrollY
			
			// Determine scroll direction
			if (currentScrollY > lastScrollY) {
				setScrollDirection('down')
			} else if (currentScrollY < lastScrollY) {
				setScrollDirection('up')
			}
			
			setLastScrollY(currentScrollY)
			window.requestAnimationFrame(calculateProgress)
		}

		window.addEventListener('scroll', handleScroll)
		handleScroll() // Инициализируем начальное состояние

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [lastScrollY])

	// Calculate animation values based on progress
	const circleScale = 0.25 + progress * 1.15
	const circleRotation = progress * 360
	
	// When scrolling up, keep logo visible and static
	const isScrollingUp = scrollDirection === 'up'
	const logoOpacity = isScrollingUp ? 1 : progress
	const logoScale = isScrollingUp ? 0.7 : 0.35 + progress * 0.35

	// When scrolling up, hide circle animation and only show logo
	const circleOpacity = isScrollingUp ? 0 : Math.min(1, 0.35 + progress * 0.65)
	const circleTransform = isScrollingUp ? 'rotate(0deg) scale(0.25)' : `rotate(${circleRotation}deg) scale(${circleScale})`
	
	const transitionClasses = isScrollingUp ? 'transition-all duration-300 ease-out' : 'transition-transform duration-300 ease-out'
	const logoTransitionClasses = 'transition-all duration-300 ease-out'

	return (
		<div
			ref={containerRef}
			className='relative h-[300vh]' // Контейнер высотой в 2 экрана для анимации
		>
			<section
				ref={sectionRef}
				className='HomeCircleAnimat sticky top-0 w-full h-[100vh] overflow-hidden flex items-center justify-center bg-[#f7f1ed]'
			>
				<div className='absolute inset-0 flex justify-center items-center overflow-hidden'>
					<div className='relative w-[180vw] h-[180vw] md:w-[200vw] md:h-[200vw]'>
						<div
							className={`absolute inset-0 ${transitionClasses}`}
							style={{
								opacity: circleOpacity,
								transform: circleTransform,
								transformOrigin: 'center',
							}}
						>
							<Image
								src='/images/circle.svg'
								alt='Animated circle'
								fill
								priority
								className='object-contain'
								sizes='(max-width: 768px) 180vw, 200vw'
							/>
						</div>
					</div>
				</div>

				<div className='absolute inset-0 flex justify-center items-center'>
					<div className='relative w-[100%] h-[100%] md:max-w-[60%] md:max-h-[60%]'>
						<div
							className={`absolute inset-0 ${logoTransitionClasses}`}
							style={{
								opacity: logoOpacity,
								transform: `scale(${logoScale})`,
								transformOrigin: 'center',
							}}
						>
							<Image
								src='/images/circle-logo.svg'
								alt='Animated logo'
								fill
								priority
								className='object-contain'
								sizes='(max-width: 768px) 80vw, 80vw'
							/>
						</div>
					</div>
				</div>
			</section>
		</div>
	)
}
